import {Button} from "@mantine/core";
import {Paper} from "@mantine/core";
import {Select} from "@mantine/core";
import {NumberInput} from "@mantine/core";
import {TextInput} from "@mantine/core";
import {Textarea} from "@mantine/core";
import {FileInput} from "@mantine/core";
import { Upload as IconUpload, Copy as IconCopy } from "@phosphor-icons/react";
import {useCallback, useContext, useMemo, useState} from "react";
import {Col, Row} from "../../components/Flex/Flex";
import Page from "../../components/Page/Page";
import MessageContext from "../../context/MessageContext";
import {useEntityTypes} from "../../utils/api";

const TEAM_A = '20:1650';
const TEAM_B = '20:1651';
const ESCAPE_POD = '2:62';
const TYPES = ['Frigates', 'Corvettes', 'Bombers', 'Gunboats', 'Fighters']
// const TYPES = ['301:2', '301:3', '301:6', '301:7', '301:13']
const BANS = [
    '2:151', '2:215', '2:229', '2:13', '2:149', '2:137', '2:12', '2:89', '2:150', //FRIGATES
    '2:112', '2:296', '2:173', '2:278', '2:241', '2:38', //CORVETTES
    '2:18', //BOMBERS
    '2:19', // FIGHTERS
];


export default function TournamentCounter() {
    const {setLoaderMessage, success, error} = useContext(MessageContext);
    const [data, setData] = useState(null);
    const {ships} = useEntityTypes(['ships'], true);

    const [maxSlots, setMaxSlots] = useState(60);
    const [teamAName, setTeamAName] = useState("");
    const [teamBName, setTeamBName] = useState("");
    const [shipInput, setShipInput] = useState("");
    const [banA, setBanA] = useState("");
    const [banB, setBanB] = useState("");




    const availableShips = useMemo(() => {
        return ships.filter(ship => TYPES.includes(ship.class) && !BANS.includes(ship.id));
    }, [ships]);
    console.log(ships, availableShips);

    const availableShipData = useMemo(() => {
        return availableShips.map(s => ({value: s.id, label: s.name}));
    }, [availableShips]);

    const onFileSelect = useCallback((file) => {
        if (!file) return;
        setLoaderMessage('Processing');
        const formData = new FormData();
        formData.append('xml', file);
        fetch(import.meta.env.VITE_BACKEND_URL + "api/processTournamentScan", {
            method: 'POST',
            body: formData,
        }).then(res => {
            if (!res.ok) {
                return res.json().then(parsed => {
                    setLoaderMessage('');
                    if (parsed?.message) {
                        error('Upload failed', parsed.message);
                    } else {
                        error('Upload failed', 'Server responded with status ' + res.status + ' - ' + res.statusText);
                    }
                    throw new Error('Upload failed');
                });
            }
            return res.json();
        }).then(data => {
            console.log(data);
            setData(data.parsed);
            setLoaderMessage('');
            success('XML loaded', '');
        }).catch(() => {
            setLoaderMessage('');
        });
    }, []);

    const shipLists = useMemo(() => extractShipLists(shipInput), [shipInput]);


    const results = useMemo(() => {
        if (!data) {
            return null;
        }

        const totals = {a: maxSlots, b: maxSlots};
        const original = {a: 0, b: 0};
        const teamA = [];
        const teamB = [];

        data.item.forEach((item) => {
            const type = ships.find((ship) => ship.id === item.typeUID);
            let points;
            if(item.typeUID === ESCAPE_POD) {
                points = 0;
            } else if(type.slotsize > 6) {
                points = 12;
            } else {
                points = type.slotsize;
            }

            let ship = {
                id: item.entityUID,
                name: item.name,
                typeName: item.typeName,
                slots: type.slotsize,
                points: points,
                isDead: false,
                isDisabled: !(item.ionic > 0),
            }

            if (item.ownerUID === TEAM_A) {
                teamA.push(ship);
                if (!ship.isDisabled) {
                    totals.b -= ship.points
                }
            } else if (item.ownerUID === TEAM_B) {
                teamB.push(ship);
                if (!ship.isDisabled) {
                    totals.a -= ship.points
                }
            }
        })

        return {
            totals,
            original,
            teamA,
            teamB,
        };

    }, [data, ships, maxSlots]);


    const formatted = useMemo(() => {
        if(!results)
            return '';
        const winner = results.totals.a > results.totals.b ? teamAName : teamBName;
        const banShipA = banA ? ships.find(s => s.id === banA) : null;
        const banShipB = banB ? ships.find(s => s.id === banB) : null;
        return `__${teamAName} vs ${teamBName}__\n**WINNER**: ${winner}! ${results.totals.a} - ${results.totals.b}\n\n**${teamAName}**\n*Ban: ${banShipA?.name ?? 'None'}*\n${formatShipList(shipLists.aurek)}\n\n**${teamBName}**\n*Ban: ${banShipB?.name ?? 'None'}*\n${formatShipList(shipLists.besh)}\n`;
    }, [results, ships, teamAName, teamBName, shipLists, banA, banB]);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(formatted).then(() => {
            success('Copied to clipboard');
        });
    };

    return (
        <Page>
            <Paper shadow="xs" p="md" withBorder>
                <p>Take an xml export of the scanner at the end of the match, upload it and fill out the name of the two teams<br />Double check the scores, then copy the markdown. Fill in the bans and picks, then post it in the dedicated thread in the coordinator area</p>
                <Row>
                    <Col>
                        <br />
                        <FileInput
                            placeholder="Browse"
                            accept="text/xml"
                            leftSection={<IconUpload size={16}/>}
                            onChange={onFileSelect}
                        />
                    </Col>
                    <Col>
                        Total Slots<br />
                        <NumberInput value={maxSlots} onChange={val => setMaxSlots(val)} />
                    </Col>
                    <Col grow={1}>
                        <Textarea
                            value={shipInput}
                            onChange={e => setShipInput(e.target.value)}
                            rows={4}
                            style={{width: '100%'}}
                            placeholder="Paste your personal event for the match start (e.g. 'You have started a showdown space battle in...'"
                            />

                    </Col>
                </Row>
            </Paper>
            <Paper shadow="xs" p="md" withBorder style={{marginBottom: '0.25rem'}}>
                <Row wrap={false}>
                    <Col basis="50%">
                        <h4>Team Aurek</h4>
                        <TextInput value={teamAName} onChange={e => setTeamAName(e.target.value)} placeholder="Team 1 Name"/>
                        <br/><br />
                        <Select data={availableShipData} value={banA} onChange={val => setBanA(val)} searchable clearable placeholder="Select banned ship" />
                        <br/><br />
                        {results && <div>Points: <b>{results.totals.a}</b></div>}
                    </Col>
                    <Col basis="50%">
                        <h4>Team Besh</h4>
                        <TextInput value={teamBName} onChange={e => setTeamBName(e.target.value)} placeholder="Team 2 Name"/>
                        <br /><br />
                        <Select data={availableShipData} value={banB} onChange={val => setBanB(val)} searchable clearable placeholder="Select banned ship" />
                        <br /><br/>
                        {results && <div>Points: <b>{results.totals.b}</b></div>}
                    </Col>
                </Row>
            </Paper>

            {results ? (
                <>

                    <Paper shadow="xs" p="md" withBorder style={{marginBottom: '0.25rem'}}>
                        <Textarea
                            value={formatted}
                            readOnly={true}
                            rows={10}
                            style={{width: '100%'}}
                            />
                        <Button
                            leftSection={<IconCopy size={16}/>}
                            onClick={copyToClipboard}
                            style={{width: '100%'}}
                        >Copy Markdown</Button>
                    </Paper>
                    <Paper shadow="xs" p="md" withBorder style={{marginBottom: '0.25rem'}}>
                        <Row wrap={false}>
                            <Col basis="50%">
                                {results.teamA.map(ship => <ShipRow key={ship.id} ship={ship}/>)}
                            </Col>
                            <Col basis="50%">
                                {results.teamB.map(ship => <ShipRow key={ship.id} ship={ship}/>)}
                            </Col>
                        </Row>
                    </Paper>
                </>
            ) : null}
        </Page>
    )


}


const ShipRow = ({ship}) => {
    let status = 'alive';
    if (ship.isDead) {
        status = 'dead';
    }
    if (ship.isDisabled) {
        status = 'disabled';
    }
    return (
        <Row mb={1}>
            <Col grow={1}>{ship.name}<br/><small>{ship.typeName}</small></Col>
            <Col>{status} - {ship.points}</Col>
        </Row>
    )
}

function extractShipLists(text) {
    const result = {
        aurek: [],
        besh: [],
    };

    const teamPatterns = {
        aurek: /Team: Showdown Aurek([\s\S]*?)(?=Team:|$)/,
        besh: /Team: Showdown Besh([\s\S]*?)(?=Team:|$)/,
    };

    for (const [key, regex] of Object.entries(teamPatterns)) {
        const match = text.match(regex);
        if (match) {
            const lines = match[1].split('\n');
            const ships = [];
            for (const line of lines) {
                const shipMatch = line.match(/-\s*\d+x\s+(.+?)(?:\s+assigned to|$)/i);
                if (shipMatch) {
                    ships.push(shipMatch[1].trim());
                }
            }
            result[key] = ships;
        }
    }

    return result;
}

function formatShipList(ships) {
    return ships.map(ship => "- "+ship).join("\n");
}
